import { CompareCard } from '@folio/components/module-elements/CompareCard';
import { useAssertedParams } from '@folio/lib';
import {
  useHasPolicyProductConfig,
  usePolicyProductConfig,
} from '@folio/utils/usePolicyProductConfig';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  keyValueFieldsToObject,
  Policy,
  policyControllerGetPolicyQueryOptions,
  PolicyDto,
} from 'korint-sdk';
import { useTranslation } from 'react-i18next';

type PolicyCompareModuleProps = {
  /**
   * The branches to compare. If undefined, the base branch will be used.
   */
  branches: Array<string | undefined>;
};

export function PolicyCompareModule({ branches }: PolicyCompareModuleProps) {
  const { t } = useTranslation();
  const { policyId } = useAssertedParams();

  const hasPolicyInformationCard = useHasPolicyProductConfig('policyInformationCard');
  if (!hasPolicyInformationCard) {
    return null;
  }

  const policyInformationCard = usePolicyProductConfig('policyInformationCard');

  const results = useQueries({
    queries: branches.map(branch => ({
      ...policyControllerGetPolicyQueryOptions(policyId, { branchId: branch }),
      select: (data: PolicyDto): Policy => ({
        ...data,
        customFields: keyValueFieldsToObject(data.customFields ?? []),
      }),
    })),
  });

  const { data: productId } = useQuery({
    ...policyControllerGetPolicyQueryOptions(policyId),
    select: data => data.productId,
  });

  if (!productId) {
    return null;
  }

  const policies = results
    .map(result => result.data)
    .filter((policy): policy is Policy => policy !== undefined);
  const isLoading = results.some(result => result.status === 'pending');

  return (
    <CompareCard
      title={t(`${productId}.policyInformationCard.title`, {
        defaultValue: policyInformationCard.title ?? '',
      })}
      productId={productId}
      fieldsConfig={policyInformationCard.fieldsConfig}
      entityType="policy"
      entities={policies}
      isLoading={isLoading}
    />
  );
}
